/**
 * Builds a whitelabel filter query parameter string.
 * Returns empty string if a city is already selected or no whitelabel is set.
 *
 * @param savedCity - The currently saved city string (e.g., "cityId=123")
 * @param savedWhitelabel - The currently saved whitelabel ID
 * @param includeAmpersand - Whether to prefix with '&' (default: true)
 * @returns Query string like "&whiteLabelId=123" or "whiteLabelId=123" or ""
 */
export const buildWhitelabelFilter = (
  savedCity: string | null,
  savedWhitelabel: number | null,
  includeAmpersand = true
): string => {
  if (savedCity || !savedWhitelabel) return "";
  return includeAmpersand
    ? `&whiteLabelId=${savedWhitelabel}`
    : `whiteLabelId=${savedWhitelabel}`;
};

/**
 * Parses a city ID from the saved city string format "cityId=123".
 * Returns null if the string is invalid or parsing fails.
 *
 * @param savedCity - The saved city string (e.g., "cityId=123")
 * @returns The parsed city ID as a number, or null if invalid
 */
export const parseCityId = (savedCity: string | null): number | null => {
  if (!savedCity) return null;
  const parts = savedCity.split("=");
  if (parts.length !== 2) return null;
  const id = parseInt(parts[1], 10);
  return isNaN(id) ? null : id;
};

/**
 * Formats a filter label for display.
 * Converts SCREAMING_SNAKE_CASE or snake_case to Title Case.
 *
 * @example formatFilterLabel('ACTIVE_STATUS') // 'Active Status'
 * @example formatFilterLabel('in_progress') // 'In Progress'
 */
export function formatFilterLabel(str: string): string {
  return str
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}
